'use client';

import { forwardRef } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

/**
 * Whether Cloudflare Turnstile is configured for this environment.
 * When false, widgets render nothing and auth flows proceed without a token
 * (Supabase only enforces CAPTCHA once enabled in the dashboard).
 */
export const isCaptchaConfigured = Boolean(SITE_KEY);

interface TurnstileWidgetProps {
  onToken: (token: string | null) => void;
  className?: string;
}

/**
 * Cloudflare Turnstile CAPTCHA widget for Supabase Auth flows.
 * Calls `onToken` with a token on success, and with `null` on expiry/error.
 * Call `ref.current?.reset()` after each submit — tokens are single-use.
 */
export const TurnstileWidget = forwardRef<TurnstileInstance, TurnstileWidgetProps>(
  function TurnstileWidget({ onToken, className }, ref) {
    if (!SITE_KEY) return null;

    return (
      <Turnstile
        ref={ref}
        siteKey={SITE_KEY}
        onSuccess={onToken}
        onExpire={() => onToken(null)}
        onError={() => onToken(null)}
        options={{ theme: 'light', size: 'flexible' }}
        className={className}
      />
    );
  }
);

export type { TurnstileInstance };
